/**
 * useViewerConfig — Admin panel hook for managing viewer configuration.
 *
 * Fetches the current config from the admin endpoint, provides save() (PUT)
 * and patch() (PATCH) methods, and manages loading/saving/error state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ViewerConfigRecord, ViewerConfigUpsertPayload } from '../server/admin/viewer-config/types';

// ── Types ────────────────────────────────────────────────────────

export interface UseViewerConfigReturn {
  config: ViewerConfigRecord | null;
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;
  save: (data: ViewerConfigUpsertPayload) => Promise<boolean>;
  patch: (data: Partial<ViewerConfigUpsertPayload>) => Promise<boolean>;
  refetch: () => void;
}

// ── API Base URL ─────────────────────────────────────────────────

function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || '';
}

// ── Hook ─────────────────────────────────────────────────────────

export function useViewerConfig(
  companyId: string,
  productId: string,
): UseViewerConfigReturn {
  const [config, setConfig] = useState<ViewerConfigRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCountRef = useRef(0);

  const fetchConfig = useCallback(async () => {
    if (!companyId || !productId) return;

    const fetchId = ++fetchCountRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(
        `${baseUrl}/api/admin/viewer-projects/${encodeURIComponent(companyId)}/` +
        `${encodeURIComponent(productId)}/config`,
        { credentials: 'include' },
      );

      if (fetchId !== fetchCountRef.current) return;

      if (response.status === 404) {
        // No config yet — that's fine
        setConfig(null);
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.error?.message || `HTTP ${response.status}`);
      }

      const json = await response.json();
      setConfig(json.data);
      setIsLoading(false);
    } catch (err) {
      if (fetchId !== fetchCountRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load configuration.');
      setIsLoading(false);
    }
  }, [companyId, productId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const save = useCallback(async (data: ViewerConfigUpsertPayload): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(
        `${baseUrl}/api/admin/viewer-projects/${encodeURIComponent(companyId)}/` +
        `${encodeURIComponent(productId)}/config`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.error?.message || `HTTP ${response.status}`);
      }

      const json = await response.json();
      setConfig(json.data);
      setIsSaving(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration.');
      setIsSaving(false);
      return false;
    }
  }, [companyId, productId]);

  const patch = useCallback(async (data: Partial<ViewerConfigUpsertPayload>): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(
        `${baseUrl}/api/admin/viewer-projects/${encodeURIComponent(companyId)}/` +
        `${encodeURIComponent(productId)}/config`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(data),
        },
      );

      if (!response.ok) {
        const json = await response.json().catch(() => null);
        throw new Error(json?.error?.message || `HTTP ${response.status}`);
      }

      const json = await response.json();
      setConfig(json.data);
      setIsSaving(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration.');
      setIsSaving(false);
      return false;
    }
  }, [companyId, productId]);

  const refetch = useCallback(() => {
    fetchConfig();
  }, [fetchConfig]);

  return { config, isLoading, error, isSaving, save, patch, refetch };
}
